/* eslint-disable @atlaskit/design-system/use-tokens-space */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-selectors */
/* eslint-disable @atlaskit/ui-styling-standard/no-styled */
/* eslint-disable @atlaskit/ui-styling-standard/no-dynamic-styles */
import type { FC } from 'react';
import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { xcss, Box, Flex } from '@atlaskit/primitives';
import { useIsReducedMotion } from '@atlaskit/motion';
import WarningIcon from '@atlaskit/icon/utility/warning';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import CloudSavedIcon from '@atlaskit/icon-lab/core/cloud-saved';

import { SaveIndicatorContext } from './SaveIndicatorContext';
import type { SaveIndicatorComponentProps } from './SaveIndicatorComponent';
import { timeFormatter } from './timeFormatter';

// time for the status message to slide in and out
const STATUS_MESSAGE_ANIMATION_DURATION = 800;
// minimum time the status will show "saving" before transitioning to "saved"
const STATUS_MESSAGE_MINIMUM_SHOW_DURATION = 2000;
// time for the status icon to slide in and out
const STATUS_ICON_ANIMATION_DURATION = 800;

export enum STATUSES {
	DISABLED = 'disabled',
	ERROR = 'error',
	SAVING = 'saving',
	SAVED = 'saved',
}

const i18n = defineMessages({
	saved: {
		id: 'save-indicator.save-indicator-live-pages.saved',
		defaultMessage: 'Saved',
		description: 'Text to display when the document has been saved',
	},
	saving: {
		id: 'save-indicator.save-indicator-live-pages.saving',
		defaultMessage: 'Saving',
		description: 'Text to display when the document is being saved',
	},
	tooltipSaved: {
		id: 'save-indicator.save-indicator-live-pages.tooltip-saved',
		description: 'Tooltip message of save indicator to indicate all changes are saved.',
		defaultMessage: 'Every change is saved. The last change was {time}.',
	},
	tooltipError: {
		id: 'save-indicator.save-indicator-live-pages.tooltip-error',
		description: 'Tooltip message of save indicator to indicate an error.',
		defaultMessage: 'We couldn’t save the latest changes. The last save was {time}.',
	},
});

const saveIndicatorStyle = xcss({
	width: '100px', // mitigate layout shift
	overflowX: 'hidden',
});

const overflowHiddenStyle = xcss({
	overflow: 'hidden',
});

const iconBoxStyle = xcss({
	width: '18px',
	height: '18px',
});

const StatusMessageAnimation = styled.div<{ status: STATUSES }>({
	display: 'flex',
	'@keyframes slideOutRight': {
		'0%': { transform: 'translateX(0)' },
		'100%': { transform: 'translateX(100%)' },
	},
	'@keyframes slideInRight': {
		'0%': { transform: 'translateX(100%)' },
		'100%': { transform: 'translateX(0)' },
	},
	'@keyframes fadeOut': {
		'0%': { opacity: 1 },
		'100%': { opacity: 0 },
	},
	animationName: ({ status }) =>
		status === STATUSES.SAVING ? 'slideInRight' : 'slideOutRight, fadeOut',
	animationDuration: `${STATUS_MESSAGE_ANIMATION_DURATION}ms`,
	animationFillMode: 'both',
	animationDelay: ({ status }) =>
		status === STATUSES.SAVED ? `${STATUS_MESSAGE_ANIMATION_DURATION}ms` : 0,
});

const StatusMessageEllipsisAnimation = styled.div<{ status: STATUSES }>({
	overflow: 'hidden',
	margin: 0,
	'@keyframes slideOutAndUpdateLayout': {
		'0%': { transform: 'translateX(0)', marginLeft: '0' },
		'100%': { transform: 'translateX(100%)', marginLeft: '-12px' },
	},
	animationName: ({ status }) =>
		status === STATUSES.SAVED ? 'slideOutAndUpdateLayout' : undefined,
	animationDuration: `${STATUS_MESSAGE_ANIMATION_DURATION}ms`,
	animationFillMode: 'both',
	animationDelay: '0ms',
});

const StatusMessage: FC<{ prevStatus: STATUSES; status: STATUSES }> = ({ prevStatus, status }) => {
	const isReducedMotion = useIsReducedMotion();
	const message = useMemo(() => {
		switch (status) {
			case STATUSES.SAVED:
				return <FormattedMessage {...i18n.saved} />;
			case STATUSES.SAVING:
				return <FormattedMessage {...i18n.saving} />;
			default:
				return null;
		}
	}, [status]);

	if ((status === STATUSES.SAVED && prevStatus !== STATUSES.SAVING) || !message) {
		return null;
	}

	return isReducedMotion ? (
		message
	) : (
		<Box xcss={overflowHiddenStyle}>
			<StatusMessageAnimation status={status}>
				{message}
				<StatusMessageEllipsisAnimation status={status}>...</StatusMessageEllipsisAnimation>
			</StatusMessageAnimation>
		</Box>
	);
};

const SavedSavingIconAnimationWrapper = styled.div<{ status: STATUSES }>({
	'@keyframes halfSlideUp': {
		'0%': { transform: 'translateY(0)' },
		'100%': { transform: 'translateY(-50%)' },
	},
	'@keyframes halfSlideDown': {
		'0%': { transform: 'translateY(-50%)' },
		'100%': { transform: 'translateY(0)' },
	},
	animationName: ({ status }) => (status === STATUSES.SAVING ? 'halfSlideUp' : 'halfSlideDown'),
	animationDuration: `${STATUS_ICON_ANIMATION_DURATION}ms`,
	animationFillMode: 'both',
	animationDelay: ({ status }) =>
		status === STATUSES.SAVING ? `${STATUS_MESSAGE_ANIMATION_DURATION}ms` : '0ms',
});

const StatusIcon: FC<{ prevStatus: STATUSES; status: STATUSES; savedTime: Date }> = ({
	prevStatus,
	status,
	savedTime,
}) => {
	const { formatMessage } = useIntl();
	const isReducedMotion = useIsReducedMotion();
	const [tooltip, setTooltip] = useState<string>('');

	const refreshTooltip = useCallback(() => {
		if (status === STATUSES.SAVING) {
			setTooltip('');
		} else if (status === STATUSES.ERROR) {
			setTooltip(
				formatMessage(i18n.tooltipError, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		} else {
			setTooltip(
				formatMessage(i18n.tooltipSaved, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		}
	}, [status, formatMessage, savedTime]);

	const reducedMotionIcon = useMemo(() => {
		if (status === STATUSES.SAVED) {
			return <CloudSavedIcon label={formatMessage(i18n.saved)} />;
		} else if (status === STATUSES.SAVING) {
			return <RefreshIcon label={formatMessage(i18n.saving)} />;
		} else if (status === STATUSES.ERROR) {
			return <WarningIcon label="Error" color={token('color.icon.subtle')} spacing="compact" />;
		} else {
			return null;
		}
	}, [status, formatMessage]);

	return (
		<Tooltip content={tooltip} position="bottom">
			<Box
				xcss={[overflowHiddenStyle, iconBoxStyle]}
				onMouseEnter={refreshTooltip}
				onFocus={refreshTooltip}
			>
				{isReducedMotion && reducedMotionIcon}
				{!isReducedMotion && (
					<>
						{(status === STATUSES.SAVING || status === STATUSES.SAVED) && (
							<SavedSavingIconAnimationWrapper status={status}>
								<Flex xcss={iconBoxStyle} justifyContent="center">
									<CloudSavedIcon label={formatMessage(i18n.saved)} />
								</Flex>
								<Flex xcss={iconBoxStyle} justifyContent="center">
									{prevStatus === STATUSES.SAVED && (
										<RefreshIcon label={formatMessage(i18n.saving)} />
									)}
								</Flex>
							</SavedSavingIconAnimationWrapper>
						)}
						{status === STATUSES.ERROR && (
							<Flex xcss={iconBoxStyle} justifyContent="center">
								<WarningIcon label="Error" color={token('color.icon.subtle')} spacing="compact" />
							</Flex>
						)}
					</>
				)}
			</Box>
		</Tooltip>
	);
};

export type SaveIndicatorLivePagesProps = Omit<SaveIndicatorComponentProps, 'formatMessage'>;

export const SaveIndicatorLivePages: FC<SaveIndicatorLivePagesProps> = ({
	editorActions,
	saveError,
	title,
	hasEmoji,
	hasCoverPicture,
	selectingEmojiTitle,
	selectingCoverPicture,
}) => {
	const [statusObj, setStatusObj] = useState<{ status: STATUSES; prevStatus: STATUSES }>({
		status: STATUSES.DISABLED,
		prevStatus: STATUSES.DISABLED,
	});
	const prevStatus = useRef<STATUSES>(statusObj.status);
	if (prevStatus.current !== statusObj.status) {
		prevStatus.current = statusObj.status;
	}

	const lastSavingTime = useRef<number>(0);
	const saveStatusDelayTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { isSaving, savedTime } = useContext(SaveIndicatorContext);

	// update status
	// MODES-4696: If you type fast enough after saving -> saved state, the status will not update to saving.
	// This is made more noticeable with this version, but it is not a regression from this version.
	useEffect(() => {
		saveStatusDelayTimeout.current && clearTimeout(saveStatusDelayTimeout.current);
		if (editorActions?.isDocumentEmpty() && !title && !hasEmoji && !hasCoverPicture) {
			setStatusObj({ status: STATUSES.DISABLED, prevStatus: prevStatus.current });
		} else if (saveError) {
			setStatusObj({ status: STATUSES.ERROR, prevStatus: prevStatus.current });
		} else if (isSaving || selectingEmojiTitle || selectingCoverPicture) {
			lastSavingTime.current = Date.now();
			setStatusObj({ status: STATUSES.SAVING, prevStatus: prevStatus.current });
		} else {
			// We are intentionally waiting to set the status to saved to extend the duration of the saving animation.
			// This reduces jarring animation transitions when the content is saved before the animation is complete.
			// This has no effect on actual saving, only the UI.
			const timeSinceLastSave = Date.now() - lastSavingTime.current;
			if (prevStatus.current === STATUSES.SAVING) {
				saveStatusDelayTimeout.current = setTimeout(
					() => setStatusObj({ status: STATUSES.SAVED, prevStatus: prevStatus.current }),
					timeSinceLastSave < STATUS_MESSAGE_MINIMUM_SHOW_DURATION
						? STATUS_MESSAGE_MINIMUM_SHOW_DURATION - timeSinceLastSave
						: 0,
				);
			} else {
				setStatusObj({ status: STATUSES.SAVED, prevStatus: prevStatus.current });
			}
		}
	}, [
		title,
		hasEmoji,
		hasCoverPicture,
		saveError,
		isSaving,
		selectingEmojiTitle,
		selectingCoverPicture,
		editorActions,
	]);

	return statusObj.status !== STATUSES.DISABLED ? (
		<Box
			paddingInlineEnd="space.100"
			role="status"
			aria-live="off"
			data-vc="save-indicator-live-pages"
		>
			<Flex xcss={saveIndicatorStyle} gap="space.050" alignItems="center" justifyContent="end">
				<StatusMessage {...statusObj} />
				<StatusIcon {...statusObj} savedTime={savedTime} />
			</Flex>
		</Box>
	) : null;
};
